import { Stack, TextField, Typography } from "@mui/material";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { MySignedCardItem } from "tng-address-collection-shared/types/apiTypes.ts";
import { searchFilter } from "../columns/searchFilter.ts";
import { useCardEditorPanel } from "../hooks/useCardEditorPanel.tsx";
import { useMySignedCards } from "../hooks/useMySignedCards.ts";
import { useMySignedCardsColumns } from "../hooks/useMySignedCardsColumns.ts";
import { useReloadCards } from "../hooks/useReloadCards.ts";
import { useUserHasActionInProjects } from "../hooks/useUserHasAction.ts";
import { useTranslator } from "../i18n/translator.ts";
import { CardsTable } from "./CardsTable.tsx";
import { CardsTableToolbar } from "./CardsTableToolbar.tsx";
import { ErrorDisplay } from "./ErrorDisplay.tsx";

const noCards: MySignedCardItem[] = [];

export const MySignedCards = () => {
  const translator = useTranslator();
  const { data, refetch, isFetching, isPending, error } = useMySignedCards();
  const cards = data?.cards ?? noCards;

  const onReload = useReloadCards(refetch);

  const { handleDetailClick, selectedCardId } = useCardEditorPanel({
    cards,
    canDelete: false,
  });

  const userHasActionIn = useUserHasActionInProjects();
  const columns = useMySignedCardsColumns(handleDetailClick);

  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  useEffect(() => {
    setRowSelection((prev) => {
      const next: RowSelectionState = { ...prev };

      cards.forEach((card) => {
        if (!(card.id in next)) {
          next[card.id] = true;
        }
      });

      return next;
    });
  }, [cards]);

  /* eslint-disable-next-line react-hooks/incompatible-library */
  const table = useReactTable<MySignedCardItem>({
    data: cards,
    getRowId: (row) => row.id,
    columns,
    getCoreRowModel: getCoreRowModel(),
    globalFilterFn: searchFilter((row) => [
      row.getValue<string>("name"),
      row.original.projectName,
      row.original.projectId,
      row.original.companyName,
      row.original.senderDisplayName,
    ]),
    state: { sorting, globalFilter, rowSelection },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  return (
    <Stack padding={2} spacing={2} height="100%">
      <Typography variant="h4">{translator("mySignedCards")}</Typography>

      <CardsTableToolbar
        filterControls={
          <TextField
            placeholder={translator("searchCardsPlaceholder")}
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ width: 400 }}
          />
        }
        onReload={onReload}
        isReloading={isFetching}
      />

      {error ? (
        <ErrorDisplay error={error} />
      ) : isPending ? null : table.getRowModel().rows.length > 0 ? (
        <CardsTable
          table={table}
          isRowSelected={(id) => selectedCardId === id}
          isRowDeemphasized={(card, cellIndex) =>
            cellIndex >= 1 &&
            !userHasActionIn(card.projectId)(ActionNames.editCard)
          }
        />
      ) : (
        <Typography color="text.secondary">
          {translator(
            cards.length > 0
              ? "mySignedCardsNoMatchingCards"
              : "mySignedCardsEmptyList",
          )}
        </Typography>
      )}
    </Stack>
  );
};
